import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Paper, List, ListItem, ListItemText, ListItemIcon, IconButton, Badge, Divider, Button } from '@mui/material';
import ChatInterface from '../../components/ChatInterface';
import { useAuth } from '../../contexts/AuthContext';
import { notificationService, NotificationData } from '../../services/NotificationService';
import { 
  Notifications as NotificationsIcon, 
  Chat as ChatIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chat-tabpanel-${index}`}
      aria-labelledby={`chat-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ChatTab: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user && tabValue === 1) {
      loadNotifications();
    }
  }, [user, tabValue]);

  const loadNotifications = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const userNotifications = await notificationService.getUserNotifications(user.uid);
      setNotifications(userNotifications.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
      
      const unread = await notificationService.getUnreadCount(user.uid);
      setUnreadCount(unread);
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await notificationService.markAsRead(notificationId);
    await loadNotifications();
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    await notificationService.markAllAsRead(user.uid);
    await loadNotifications();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1">
          Veuillez vous connecter pour accéder au chat.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="chat et notifications"
          sx={{
            '& .MuiTab-root': {
              color: '#C9B6AB',
              '&.Mui-selected': {
                color: '#C9B6AB',
                fontWeight: 'bold'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#C9B6AB'
            }
          }}
        >
          <Tab 
            icon={<ChatIcon />} 
            iconPosition="start"
            label="Messages" 
            id="chat-tab-0"
            aria-controls="chat-tabpanel-0"
          />
          <Tab 
            icon={
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            }
            iconPosition="start"
            label="Notifications" 
            id="chat-tab-1"
            aria-controls="chat-tabpanel-1"
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ height: 'calc(100vh - 200px)', overflow: 'hidden' }}>
          <ChatInterface 
            currentUserId={user.uid}
            currentUserName={user.displayName || user.email || 'Utilisateur'}
          />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
          {loading ? (
            <Typography variant="body2" sx={{ p: 3, textAlign: 'center', color: '#C9B6AB' }}>
              Chargement des notifications...
            </Typography>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 64, color: '#C9B6AB', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ color: '#C9B6AB', mb: 1 }}>
                Aucune notification
              </Typography>
              <Typography variant="body2" sx={{ color: '#999' }}>
                Vous n'avez pas encore de notifications
              </Typography>
            </Box>
          ) : (
            <>
              {unreadCount > 0 && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={handleMarkAllAsRead}
                    sx={{ color: '#C9B6AB' }}
                  >
                    Tout marquer comme lu
                  </Button>
                </Box>
              )}
              <List>
                {notifications.map((notif, index) => (
                  <React.Fragment key={notif.id}>
                    <ListItem
                      sx={{
                        backgroundColor: notif.isRead ? 'transparent' : '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#fafafa'
                        }
                      }}
                    >
                      <ListItemIcon>
                        {notif.isRead ? (
                          <NotificationsIcon sx={{ color: '#999' }} />
                        ) : (
                          <Badge variant="dot" color="error">
                            <NotificationsIcon sx={{ color: '#C9B6AB' }} />
                          </Badge>
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: notif.isRead ? 'normal' : 'bold',
                              color: notif.isRead ? '#666' : '#333'
                            }}
                          >
                            {notif.title}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="body2"
                              sx={{ color: notif.isRead ? '#999' : '#666', mt: 0.5 }}
                            >
                              {notif.message}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: '#999', mt: 0.5, display: 'block' }}
                            >
                              {new Date(notif.createdAt).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          </>
                        }
                      />
                      {!notif.isRead && (
                        <IconButton
                          size="small"
                          onClick={() => handleMarkAsRead(notif.id)}
                          sx={{ color: '#C9B6AB' }}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItem>
                    {index < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </Box>
      </TabPanel>
    </Box>
  );
};

export default ChatTab; 